'use client';

import { dataLayerPush, getBaseUrlJoined, trackMainNavLinkClick } from '@libs/analytics';
import { usePageLayout } from '@libs/common-next';
import { handleServiceClick } from '@libs/experience-api';

import Link from 'next/link';
import type { MouseEvent } from 'react';
import type { Card } from '../CardUp/types';
import { MarketingSection } from './MarketingSection';
import { MenuCard } from './MenuCard';
import { RecursiveMenu } from './RecursiveMenu';
import { TabbedMenu } from './TabbedMenu';

import type { CategoryTypes, SubMenuProps } from './types';

import { shouldRenderSubMenu } from './utils/shouldRenderSubMenu';
import { getCategoryPath, trackCategoryPath, trackingData } from './utils';

function RenderMenuType({
    type,
    mappedCategory,
    parentCategory,
    path,
}: {
    type?: string;
    mappedCategory: CategoryTypes;
    parentCategory: string | undefined;
    path: string[];
}) {
    const { isAuthenticated } = usePageLayout();

    const { cards, categories, displayText, link, rightMenuCard, specialType } = mappedCategory;

    function handleOnClick(event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) {
        handleServiceClick({
            authType: isAuthenticated,
            event,
            link,
        });

        const categoryPath = getCategoryPath(path, displayText);
        const categoryPathTrack = trackCategoryPath(categoryPath);
        trackMainNavLinkClick(categoryPathTrack);

        dataLayerPush(
            trackingData({
                selection: categoryPathTrack,
                link_url: getBaseUrlJoined(link),
            })
        );
    }

    switch (type) {
        case 'tabs':
            return categories ? (
                <TabbedMenu parentCategories={categories} parentCategory={parentCategory} path={path} />
            ) : null;
        case 'card':
            return cards ? (
                <>
                    <ul className={`submenu-container__list card-list ${cards.length < 6 ? 'larger-cards' : ''}`}>
                        {cards.map((card: Card) => (
                            <MenuCard
                                category={parentCategory}
                                card={card}
                                key={card.header}
                                onClick={() => {
                                    trackMainNavLinkClick([...path, card.header].join(' | '));
                                }}
                            />
                        ))}
                    </ul>
                    {link && (
                        <Link
                            data-testid="service-link"
                            href={link}
                            onClick={event => handleOnClick(event)}
                            prefetch={false} // https://petsmart.atlassian.net/browse/SB-325
                        >
                            {displayText}
                            {' >'}
                        </Link>
                    )}
                </>
            ) : null;
        default:
            return categories ? (
                <>
                    <ul className="submenu-container__list">
                        <RecursiveMenu
                            parentCategory={parentCategory}
                            parentCategories={categories}
                            path={path}
                            wrapListItems={false}
                        />
                    </ul>
                    <MarketingSection
                        rightMenuCard={rightMenuCard}
                        childCategories={categories}
                        specialType={specialType}
                        path={path}
                    />
                </>
            ) : null;
    }
}

export function SubMenu({
    mappedCategory,
    type,
    specialType,
    parentCategory,
    path = [],
    menuDropdownStyle = false,
}: Readonly<SubMenuProps>) {
    const shouldRender = shouldRenderSubMenu(mappedCategory, type);
    if (!shouldRender) {
        return null;
    }

    return (
        <div
            className={`submenu-container ${
                specialType?.toLowerCase().replaceAll(' ', '-') || ''
            }${menuDropdownStyle ? ' narrow' : ''}`}
        >
            <div className={`submenu__layout submenu__layout--${type}`} data-testid="submenu-navigation">
                <RenderMenuType
                    {...{
                        type,
                        mappedCategory,
                        parentCategory,
                        path,
                    }}
                />
            </div>
        </div>
    );
}
